/**
 * CriticalStrip — the always-visible facts above the tabs.
 *
 * THE BIJECTION — six costumes, six meanings, no overlap either way:
 *   - neutral pill  = a recorded neutral state        (DNR + DNI)
 *   - affirm pill   = a confirmed positive            (Consented, No known allergies)
 *   - risk pill     = a recorded state that GATES you (Declined)
 *   - absent pill   = no value yet — ONE hollow chip (variant="absent"),
 *                     byte-identical in every cell and never italic. ONE
 *                     ruled exception to its ink: an active SPICE-only
 *                     client's missing emergency contact wears the
 *                     clinical-warning ink in the same hollow vessel
 *                     (variant="clinical-warn") — the therapist visits
 *                     alone and must be able to reach someone. Code
 *                     status is not required of a SPICE-only client and
 *                     never wears it.
 *   - sage text     = a person (care managers, SPICE therapists, an
 *                     emergency contact — up to two shown, primary
 *                     first, "+N more" opening People beyond that)
 *   - clinical-warning text = dangerous CONTENT (the allergen list) — same
 *                     value weight as the names; the ink alone is the signal
 *
 * Anatomy: every fact cell is two lines. Line 1 = the label alone. Line
 * 2 = the value, a rem-locked line every pill centers inside (nothing a
 * value does can change its cell's height) — the cell's one write door
 * sits at the end of THIS line, beside the value it acts on, never on the
 * label. Markers (phone, authority roles, instruments on file) ride after
 * the value at the 12px tier, each in its own register's ink. A staff
 * slot (care managers, SPICE therapists) is the one cell that grows: the
 * label, then each person as the name alone followed by the roster phone
 * line with that person's door at its end — the lead first with its
 * Assign/Change door, then each additional person by name with a quiet
 * Remove — and a last line holding the slot's quiet Add. A name never
 * breaks to make room for its door. The label reads plural when the slot
 * holds more than one person. Provenance is OUT of the strip entirely:
 * the strip answers what is true now; the owning cards keep the receipts.
 *
 * Order: CARE TEAM first (care managers and/or SPICE therapists, by
 * engagement — see the first cell below), then EMERGENCY CONTACT, CODE
 * STATUS, TRANSPORT CONSENT (the care manager's fact, but SPICE plans
 * around it too — the same cell shows on every face), and ALLERGIES
 * last ("not the most important information, but information you
 * need"), most severe first, then alphabetical (the ONE order every
 * allergy list on the record shares — src/lib/allergySort.ts), the
 * severity riding after the substance as a marker ("Penicillin ·
 * severe") when one is actually stated. CONDITIONS is not a strip
 * fact — it stays on the Care record tab's own card.
 *
 * Width: a client on both services holds two staff slots in the first
 * cell, so the pair's container carries a minimum width from xl up
 * (xl:min-w-[20rem]); a grid item's min-width is the least its track can
 * be, so the first track grows to fit the pair and the other four cells
 * share what remains in their usual proportions. The minimum is the
 * smallest that keeps every label on one line and lets a phone and its
 * door share the phone line; the pair's own gaps stay tight for the same
 * reason. Below xl the two slots stack instead of squeezing. More people
 * in a slot grow it downward, one stacked person at a time, never wider.
 *
 * Doors: Assign/Change (the lead care manager opens the care manager
 * dialog, whose primary follows the choice: Change, Remove or Assign
 * care manager; the lead SPICE therapist opens Change service), Add and
 * Remove (additional care managers and SPICE therapists — Add only once
 * the slot has a lead; a Remove leaves "{Name} is off this client." with
 * Undo at the end of the slot for 30 seconds, held by the strip so the
 * record's re-read does not clear it), Add emergency contact (none on
 * file: opens Add contact in place with Emergency contact ticked, gated
 * as the Contacts card's own Add), Record/Change (consent: a two-way
 * toggle, nothing on until recorded), Record/Change (code status — the
 * record dialog on the SAME legal-instruments row the Care record's
 * Legal & wishes card edits, showing the set, Status and Effective only;
 * a first record is seeded with the code_status instrument, an edit
 * never sends it). Every door reads in the ONE sage text-action ink,
 * whether or not its fact is absent — an absent care manager is a gap, a
 * missing emergency contact or unrecorded consent gates action, but the
 * door itself never alarms; the fact beside it (an absent pill, a risk
 * pill) carries that signal instead. Allergies carries no door; that
 * write belongs to the Care record. A SPICE-only viewer (does SPICE, not
 * care management) never sees these doors at all — the facts stay
 * readable.
 *
 * The dialogs' primaries are pressable at rest and name their outcome.
 * Pressed with nothing changed, one closes and sends nothing; pressed
 * with a gap, it sends nothing, shows the gap's sentence under its box
 * and moves focus there. Copy that speaks of the client uses the
 * client's recorded pronouns (recordFormsCopy). A refusal the server
 * names reads its own sentence; anything else the house save sentence.
 *
 * 200% zoom: care managers run their phones at 200%, so every pinned
 * metric here is REM — the chip pin is h-[1.125rem], the value line
 * min-h-[1.375rem]; no px heights.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Chip } from '@/components/primitives/Chip';
import { Button } from '@/components/primitives/Button';
import { Dialog } from '@/components/primitives/Dialog';
import { Field, fieldBoxAria } from '@/components/primitives/Field';
import { SelectField } from '@/components/primitives/SelectField';
import { SegmentedToggle, type SegmentedOption } from '@/components/primitives/SegmentedToggle';
import { formatPhoneDisplay, normalizePhone } from '@/lib/phone';
import { isSpiceOnlyViewer } from '@/lib/spiceOnlyViewer';
import { type PayloadFieldSpec, TRANSPORTATION_CONSENT_VALUES } from '@/lib/noteVocabulary';
import { useTenant } from '@/lib/auth';
import {
  canAssignCareManagers,
  canEditClientDetails,
  canWriteClinicalRecord,
} from '@/lib/capabilities';
import { useAssignOptions } from '@/lib/workspace';
import {
  useAddClientStaff,
  useAssignClientCm,
  useRemoveClientStaff,
  type ClientStaffService,
} from '@/lib/clientsApi';
import { allergySeverityMarker, sortAllergiesBySeverity } from '@/lib/allergySort';
import { displayValue } from '@/lib/valueCase';
import { withLead } from '@/lib/changeService';
import { contactSentence, errorCodeOf, pageReadText, pageWriteText, type SupportContact } from '@/lib/errorCopy';
import { fieldSentence } from '@/lib/fieldErrorCopy';
import { focusFirstGap } from '@/lib/formGaps';
import { addStaffIntro, assignCmIntro, codeStatusIntro, consentIntro } from '@/lib/recordFormsCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import {
  patchTransportationConsent,
  recordEditErrorMessage,
} from '@/lib/clientRecordEditApi';
import {
  codeStatusSetLabel,
  contactEffective,
  type ClientPronouns,
  type ClientRecordSnapshot,
  type TransportationConsent,
} from '@/lib/clientRecord';
import type { RecordServiceSide } from '@/lib/recordServiceSide';
import { ChangeServiceDialog } from '@/components/clientRecord/ChangeServiceDialog';
import { RowEditDialog } from '@/components/clientRecord/RowEditDialog';
import { registryValuesFromRow } from '@/components/clientRecord/cardSpine';

/** People are content: sage text, decoration on hover ONLY (an underline
 *  at rest fought the door verbs). The SAME ink as the doors below it
 *  (both read as "acting in the forest register", never a second color). */
const LINK_CLS =
  'font-medium text-sage-700 underline-offset-2 hover:underline hover:decoration-sage-700 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-tight';

/** The strip-local chip pin, in REM (200% zoom): 1.125rem = 18px at the
 *  default root, centered inside the value line's 1.375rem box. The
 *  global one-chip-height pin is a separate ruled pass. */
const STRIP_CHIP = '!inline-flex h-[1.125rem] items-center !py-0';

/** The marker tier: 12px equivalents in rem; each item keeps its own
 *  register's ink (the slot is a size, not a colour). */
const MARKER_CLS = 'text-xs leading-[1.125rem]';

/** A door: the ONE sage text-action ink, 11px-tier verb, sitting beside
 *  the value it acts on (never the label). No alarm ink here — a fact
 *  that gates action carries its own signal (an absent pill, a risk
 *  pill); the door that opens it stays quiet. */
// !text-label: the Button primitive injects its own size (SIZE_CLASSES
// sm = 0.8125rem) which wins the cascade by stylesheet order, so every
// door is pinned to the label tier explicitly.
// before:hidden: the Button primitive extends its hit area 4px above and
// below with a pseudo-element; on the value line that extension runs past
// the cell's bottom edge and reads as clipped content. A door is a small
// text action inside a dense strip, so it keeps the box it is drawn in.
// The height is a minimum, not a pin: a door of several words ("Add
// emergency contact") wraps between words where its line is narrower
// than its text (a phone at 200%) and grows, with its lines set right,
// at the end of the value line where every door sits; a one-word door
// measures the same as before.
const DOOR_BTN_CLS =
  '!min-h-[1.25rem] !px-1 !py-0 !text-label !leading-tight text-right before:hidden font-semibold text-sage-700 hover:!text-forest-700';

/** A door rendered as a Link (navigates rather than opens a dialog) —
 *  the same ink and size tier as DOOR_BTN_CLS's Buttons. */
const DOOR_LINK_CLS =
  'inline-flex h-[1.25rem] items-center text-label font-semibold text-sage-700 underline-offset-2 hover:underline ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-tight';

/** Acronym title-text expansions (0136 code-status values + the 0150
 *  authority markers) — the acronym stays the visible word; a reader who
 *  doesn't know it can hover for the plain-English form. */
const ACRONYM_TITLES: Record<string, string> = {
  poa: 'Power of attorney',
  hc_proxy: 'Healthcare proxy',
  dnr: 'Do not resuscitate',
  dni: 'Do not intubate',
  dnh: 'Do not hospitalize',
  polst: 'Physician orders for life-sustaining treatment',
  dpoa: 'Durable power of attorney',
};

/** How long a slot shows a Remove's outcome (the Undo line, a refusal). */
const REMOVE_NOTICE_MS = 30_000;

/** The code-status dialog draws these of the legal-instrument fields, in
 *  this order; the instrument is fixed and the designee does not apply. */
const CODE_STATUS_FIELDS = ['code_status_values', 'status', 'effective_on'] as const;

/** A roster person on the strip: the lead or an additional person. */
interface StaffPerson {
  rosterId: string;
  displayName: string;
  phone: string | null;
}

function FactCell({
  testId,
  label,
  door,
  children,
}: {
  testId: string;
  label: string;
  /** The cell's one write door — rendered on the VALUE line, beside the
   *  fact it acts on. */
  door?: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <div data-testid={testId} className="min-w-0 lg:px-5 lg:first:pl-0 lg:last:pr-0">
      {/* A rem label-line box: doored and doorless cells measure identical
          at any given zoom (one height at the default root — the census
          law). */}
      <div className="flex min-h-[1.25rem] items-center">
        <span className="text-label tracking-label font-semibold uppercase text-sage-400">
          {label}
        </span>
      </div>
      {/* The locked value line: pills (1.125rem) center inside it; text
          sits on it. Nothing a value does changes the cell's height. The
          door sits at the end of this SAME line (ml-auto), with the value
          it acts on rather than floating above on the label. */}
      <div className="mt-0.5 flex min-h-[1.375rem] flex-wrap items-center gap-x-2 gap-y-0.5 text-value">
        {children}
        {door && <span className="ml-auto flex items-center">{door}</span>}
      </div>
    </div>
  );
}

/** The ONE absence costume (v3): the hollow chip, identical everywhere.
 *  Copy is per-cell ("Not assigned" / "Not recorded") but the vessel
 *  never varies; the one ruled exception (the emergency contact of an
 *  active SPICE-only client) keeps this vessel and changes only its ink,
 *  in the cell below. */
function AbsentValue({ children, testId }: { children: string; testId?: string }): ReactNode {
  return (
    <Chip variant="absent" className={STRIP_CHIP} data-testid={testId}>
      {children}
    </Chip>
  );
}

/** A roster person's work phone, as a quiet marker-tier line with a
 *  tel: link — rendered only when the roster has one on file. Shared by
 *  every person in the care-manager and SPICE-therapist slots below. */
function StaffPhoneLine({ phone, testId }: { phone: string | null; testId: string }): ReactNode {
  if (phone === null || phone === '') return null;
  return (
    <a
      href={`tel:${normalizePhone(phone)}`}
      data-testid={testId}
      className={`${MARKER_CLS} block text-ink-soft hover:text-sage-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-tight`}
    >
      {formatPhoneDisplay(phone)}
    </a>
  );
}

const CONSENT_LABELS: Record<TransportationConsent, string> = {
  consented: 'Consented',
  declined: 'Declined',
};

/** The consent route's one field, as a registry-shaped spec, so a refusal
 *  reads the strip's own label and choices. */
const CONSENT_SPEC: Readonly<Record<string, PayloadFieldSpec>> = {
  transportation_consent: { kind: 'enum', values: TRANSPORTATION_CONSENT_VALUES },
};

/** The toggle's two sides: Consented, then Declined. */
const CONSENT_OPTIONS: readonly [SegmentedOption<TransportationConsent>, SegmentedOption<TransportationConsent>] = [
  { value: 'consented', label: CONSENT_LABELS.consented, suffix: 'consented' },
  { value: 'declined', label: CONSENT_LABELS.declined, suffix: 'declined' },
];

const CONSENT_ID = 'transportation-consent';

/** The consent dialog: a two-way toggle, nothing on until a consent is
 *  recorded ("Not recorded" is the absence of a value and cannot be
 *  saved). "Save consent" whatever the toggle shows; pressed with nothing
 *  on it shows the one-of sentence under the toggle and moves focus to
 *  its first radio; pressed unchanged it closes. */
function TransportationConsentDialog({
  current,
  pronouns,
  onClose,
}: {
  current: TransportationConsent | null;
  pronouns: ClientPronouns | null;
  onClose: () => void;
}): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const queryClient = useQueryClient();
  // The error copy's contact, from the shell's cached tenant config.
  const contact = useSupportContact({ enabled: false });
  const [value, setValue] = useState<TransportationConsent | null>(current);
  const [gap, setGap] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: (consent: TransportationConsent) => patchTransportationConsent(tenantSlug, clientId, consent),
    onSuccess: async () => {
      // ONE audited re-read of the aggregate after the write.
      await queryClient.invalidateQueries({ queryKey: ['clientRecord'] });
      onClose();
    },
    onError: (err, consent) =>
      setError(
        recordEditErrorMessage(err, {
          specs: CONSENT_SPEC,
          sent: { transportation_consent: consent },
          labels: { transportation_consent: 'Consent' },
          valueLabels: { transportation_consent: CONSENT_LABELS },
        }, contact),
      ),
  });

  function pressSave(): void {
    if (value === null) {
      setGap(fieldSentence('Consent', { kind: 'one_of', values: CONSENT_OPTIONS.map((o) => o.label) }));
      focusFirstGap([`${CONSENT_ID}-${CONSENT_OPTIONS[0].suffix}`]);
      return;
    }
    if (value === current) {
      onClose();
      return;
    }
    setError(null);
    saveMut.mutate(value);
  }

  const aria = fieldBoxAria(CONSENT_ID, { required: true, error: gap !== null });

  return (
    <Dialog
      title="Transportation consent"
      size="sm"
      testId="transportation-consent-dialog"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={saveMut.isPending}
            loading={saveMut.isPending}
            data-testid="transportation-consent-save"
            onClick={pressSave}
          >
            Save consent
          </Button>
        </>
      }
    >
      <p className="text-xs text-ink-soft mb-3">{consentIntro(pronouns)}</p>
      <Field label="Consent" required group htmlFor={CONSENT_ID} error={gap ?? undefined}>
        <SegmentedToggle
          options={CONSENT_OPTIONS}
          value={value}
          onChange={(next) => {
            setGap(null);
            setError(null);
            setValue(next);
          }}
          name="Consent"
          labelledBy={`${CONSENT_ID}-label`}
          describedBy={aria['aria-describedby']}
          invalid={aria['aria-invalid'] === 'true'}
          required
          testIdPrefix={CONSENT_ID}
          id={CONSENT_ID}
        />
      </Field>
      {error !== null && (
        <p role="alert" data-testid="transportation-consent-error" className="mt-2 text-xs text-pepper">
          {error}
        </p>
      )}
    </Dialog>
  );
}

const CM_REFUSED = "That person isn't an active care manager. Pick someone else from the list.";
/** The seat itself may not assign: it is not a care manager. */
const ASSIGNER_REFUSED = "Only a care manager can change this client's care managers. Ask one of them to make the change.";
const THERAPIST_REFUSED = "That person isn't an active SPICE therapist. Pick someone else from the list.";

/** The slot's words for a service: what the lead is called and what
 *  the list of an additional person's clients is called. */
const SERVICE_WORDS: Record<ClientStaffService, { person: string; lead: string; list: string }> = {
  care_management: { person: 'care manager', lead: 'lead care manager', list: 'caseload' },
  spice: { person: 'SPICE therapist', lead: 'lead SPICE therapist', list: 'SPICE clients' },
};

/** The care manager dialog's primary, by the choice: Assign with no lead
 *  on file; otherwise Remove for No care manager, else Change. */
function assignCmPrimary(hasLead: boolean, rosterId: string): string {
  if (!hasLead) return 'Assign care manager';
  return rosterId === '' ? 'Remove care manager' : 'Change care manager';
}

/**
 * The care manager dialog: active care managers come from the ONE
 * assign-options source, with the lead on file shown even when the list
 * no longer holds her. The title and the primary follow the state
 * (assignCmPrimary). Pressed on the current lead it closes and sends
 * nothing. A refused person reads the house sentence; anything else the
 * house save sentence.
 */
function AssignCmDialog({
  current,
  pronouns,
  contact,
  onClose,
}: {
  /** The lead care manager on file. */
  current: StaffPerson | null;
  pronouns: ClientPronouns | null;
  contact: SupportContact;
  onClose: () => void;
}): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const { careManagers, isLoading, isError, error: optionsError } = useAssignOptions(tenantSlug);
  const assign = useAssignClientCm(tenantSlug);
  const options = withLead(careManagers, current);
  // N4: never an empty select. While the list loads, a line; when its read
  // fails, the house sentence; with nobody to choose, a line saying so.
  const listState: 'loading' | 'failed' | 'none' | 'ready' = isLoading
    ? 'loading'
    : isError
      ? 'failed'
      : options.length === 0
        ? 'none'
        : 'ready';
  const currentRosterId = current?.rosterId ?? '';
  const [rosterId, setRosterId] = useState(currentRosterId);
  const [error, setError] = useState<string | null>(null);
  const hasLead = current !== null;

  async function pressSave(): Promise<void> {
    if (rosterId === currentRosterId) {
      onClose();
      return;
    }
    setError(null);
    try {
      await assign.mutateAsync({ id: clientId, rosterId: rosterId === '' ? null : rosterId });
      onClose();
    } catch (err) {
      const code = errorCodeOf(err);
      setError(
        code === 'not_a_care_manager' ? CM_REFUSED : code === 'assigner_not_care_manager' ? ASSIGNER_REFUSED : pageWriteText(err, contact),
      );
    }
  }

  return (
    <Dialog
      title={hasLead ? 'Change care manager' : 'Assign care manager'}
      size="sm"
      testId="assign-cm-dialog"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={assign.isPending || listState === 'failed' || listState === 'none'}
            loading={assign.isPending}
            data-testid="assign-cm-save"
            onClick={() => void pressSave()}
          >
            {assignCmPrimary(hasLead, rosterId)}
          </Button>
        </>
      }
    >
      <p className="text-xs text-ink-soft mb-3">{assignCmIntro(pronouns)}</p>
      {listState === 'loading' && (
        <p className="text-sm text-ink-soft" data-testid="assign-cm-loading">
          Loading care managers…
        </p>
      )}
      {listState === 'failed' && (
        <p role="alert" className="text-sm text-pepper" data-testid="assign-cm-options-error">
          {pageReadText(optionsError, contact, 'the care managers')}
        </p>
      )}
      {listState === 'none' && (
        <p className="text-sm text-ink-soft" data-testid="assign-cm-none">
          {contactSentence('No care managers are set up yet. Ask {contact_admin} to add one on People.', contact)}
        </p>
      )}
      {(listState === 'ready' || listState === 'loading') && (
      <Field label="Care manager" htmlFor="assign-cm-select">
        <SelectField
          size="field"
          id="assign-cm-select"
          data-testid="assign-cm-select"
          value={rosterId}
          onChange={(e) => {
            setError(null);
            setRosterId(e.target.value);
          }}
        >
          <option value="">No care manager</option>
          {options.map((cm) => (
            <option key={cm.roster_id} value={cm.roster_id}>
              {cm.name}
            </option>
          ))}
        </SelectField>
      </Field>
      )}
      {error !== null && (
        <p role="alert" data-testid="assign-cm-error" className="mt-2 text-xs text-pepper">
          {error}
        </p>
      )}
    </Dialog>
  );
}

/** The sentence for a refused add of an additional person; null when the
 *  server named no reason this dialog knows. */
function addStaffRefusal(err: unknown, service: ClientStaffService): string | null {
  const code = errorCodeOf(err);
  if (code === 'already_the_lead') return `That person is already the ${SERVICE_WORDS[service].lead}. Pick someone else to add.`;
  if (code === 'not_a_care_manager') return CM_REFUSED;
  if (code === 'assigner_not_care_manager') return ASSIGNER_REFUSED;
  if (code === 'not_a_spice_therapist') return THERAPIST_REFUSED;
  return null;
}

/**
 * The Add dialog for an additional care manager or SPICE therapist. Care
 * managers come from the assign-options source's care_managers, SPICE
 * therapists from its spice_therapists (the same source the Change
 * service dialog reads); anyone already on the client for this service,
 * the lead included, is left out. Pressed with nothing chosen it shows
 * the from-list sentence under the select and moves focus there.
 */
function AddClientStaffDialog({
  service,
  onClientRosterIds,
  pronouns,
  contact,
  onClose,
}: {
  service: ClientStaffService;
  /** The lead and every additional person already on the client for this service. */
  onClientRosterIds: readonly string[];
  pronouns: ClientPronouns | null;
  contact: SupportContact;
  onClose: () => void;
}): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const { careManagers, spiceTherapists, isLoading, isError, error: optionsError } = useAssignOptions(tenantSlug);
  const add = useAddClientStaff(tenantSlug);
  const [rosterId, setRosterId] = useState('');
  const [gap, setGap] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isCareManagement = service === 'care_management';
  const label = isCareManagement ? 'Care manager' : 'SPICE therapist';
  const words = SERVICE_WORDS[service];
  const eligible = (isCareManagement ? careManagers : spiceTherapists).filter(
    (p) => !onClientRosterIds.includes(p.roster_id),
  );
  // N4: never an empty select (the Assign dialog's three states).
  const listState: 'loading' | 'failed' | 'none' | 'ready' = isLoading
    ? 'loading'
    : isError
      ? 'failed'
      : eligible.length === 0
        ? 'none'
        : 'ready';

  async function pressAdd(): Promise<void> {
    if (rosterId === '') {
      setGap(fieldSentence(label, { kind: 'from_list' }));
      focusFirstGap(['add-client-staff-select']);
      return;
    }
    setError(null);
    try {
      await add.mutateAsync({ id: clientId, rosterId, service });
      onClose();
    } catch (err) {
      setError(addStaffRefusal(err, service) ?? pageWriteText(err, contact));
    }
  }

  return (
    <Dialog
      title={`Add a ${words.person}`}
      size="sm"
      testId="add-client-staff-dialog"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={add.isPending || listState === 'failed' || listState === 'none'}
            loading={add.isPending}
            data-testid="add-client-staff-save"
            onClick={() => void pressAdd()}
          >
            Add {words.person}
          </Button>
        </>
      }
    >
      <p className="text-xs text-ink-soft mb-3">{addStaffIntro(service, pronouns)}</p>
      {listState === 'loading' && (
        <p className="text-sm text-ink-soft" data-testid="add-client-staff-loading">
          {isCareManagement ? 'Loading care managers…' : 'Loading SPICE therapists…'}
        </p>
      )}
      {listState === 'failed' && (
        <p role="alert" className="text-sm text-pepper" data-testid="add-client-staff-options-error">
          {pageReadText(optionsError, contact, isCareManagement ? 'the care managers' : 'the SPICE therapists')}
        </p>
      )}
      {listState === 'none' && (
        <p className="text-sm text-ink-soft" data-testid="add-client-staff-none">
          {isCareManagement
            ? 'Everyone who manages care is already on this client.'
            : 'Every SPICE therapist is already on this client.'}
        </p>
      )}
      {(listState === 'ready' || listState === 'loading') && (
      <Field label={label} htmlFor="add-client-staff-select" error={gap ?? undefined}>
        <SelectField
          size="field"
          id="add-client-staff-select"
          data-testid="add-client-staff-select"
          value={rosterId}
          {...fieldBoxAria('add-client-staff-select', { error: gap !== null })}
          onChange={(e) => {
            setGap(null);
            setError(null);
            setRosterId(e.target.value);
          }}
        >
          <option value="">Choose a {words.person}</option>
          {eligible.map((p) => (
            <option key={p.roster_id} value={p.roster_id}>
              {p.name}
            </option>
          ))}
        </SelectField>
      </Field>
      )}
      {error !== null && (
        <p role="alert" data-testid="add-client-staff-error" className="mt-2 text-xs text-pepper">
          {error}
        </p>
      )}
    </Dialog>
  );
}

/** What a slot shows at its end after a Remove: the Undo line while the
 *  removed person can be put back, and the sentence of a refusal. Held by
 *  the strip for 30 seconds, so the record's re-read leaves it. */
interface RemoveNotice {
  person: StaffPerson;
  /** Whether "{Name} is off this client." with Undo shows. */
  undo: boolean;
  error: string | null;
}

/** The sentence for a Remove the server refused. */
function removeRefusal(err: unknown, person: StaffPerson, contact: SupportContact): string {
  const code = errorCodeOf(err);
  if (code === 'assignment_not_found') return `${person.displayName} was already off this client, so nothing changed. There's nothing more to do.`;
  if (code === 'assigner_not_care_manager') return ASSIGNER_REFUSED;
  return pageWriteText(err, contact);
}

/** The sentence for an Undo the server refused. */
function undoRefusal(err: unknown, person: StaffPerson, service: ClientStaffService, contact: SupportContact): string {
  const code = errorCodeOf(err);
  const words = SERVICE_WORDS[service];
  if (code === 'already_the_lead') return `${person.displayName} is already back as the ${words.lead}. There's nothing more to do.`;
  if (code === 'not_a_care_manager' || code === 'not_a_spice_therapist') {
    return `${person.displayName} is no longer an active ${words.person}, so they weren't put back. Add someone else instead.`;
  }
  return pageWriteText(err, contact);
}

/** The quiet Remove door on one additional person: deactivates her for
 *  this service and re-reads the record. The outcome, put back or
 *  refused, is the strip's to show (RemoveNotice). */
function RemoveStaffDoor({
  person,
  service,
  testId,
  onRemoved,
  onRefused,
}: {
  person: StaffPerson;
  service: ClientStaffService;
  testId: string;
  onRemoved: (person: StaffPerson) => void;
  onRefused: (person: StaffPerson, err: unknown) => void;
}): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const remove = useRemoveClientStaff(tenantSlug);
  return (
    <Button
      variant="ghost"
      size="sm"
      className={DOOR_BTN_CLS}
      data-testid={testId}
      disabled={remove.isPending}
      aria-label={`Remove ${person.displayName}`}
      onClick={() =>
        remove.mutate(
          { id: clientId, rosterId: person.rosterId, service },
          { onSuccess: () => onRemoved(person), onError: (err) => onRefused(person, err) },
        )
      }
    >
      Remove
    </Button>
  );
}

/** The slot's Undo line and refusal sentence, at the end of the slot. */
function RemoveNoticeLine({
  notice,
  undoPending,
  onUndo,
}: {
  notice: RemoveNotice;
  undoPending: boolean;
  onUndo: () => void;
}): ReactNode {
  return (
    <div className="flex flex-col gap-y-0.5">
      {notice.undo && (
        <p role="status" data-testid="strip-remove-undo-line" className={`${MARKER_CLS} flex flex-wrap items-center gap-x-1 text-ink-soft`}>
          {notice.person.displayName} is off this client.
          <Button
            variant="ghost"
            size="sm"
            className={DOOR_BTN_CLS}
            data-testid="strip-remove-undo"
            disabled={undoPending}
            onClick={onUndo}
          >
            Undo
          </Button>
        </p>
      )}
      {notice.error !== null && (
        <p role="alert" data-testid="strip-remove-undo-error" className={`${MARKER_CLS} text-pepper`}>
          {notice.error}
        </p>
      )}
    </div>
  );
}

/**
 * One staff slot: the label, then each person as the name alone (or the
 * absent chip for a missing lead) followed by the phone line — the
 * roster's work phone when on file, with that person's door at the end of
 * the line — and a last line for the slot's Add door. The door never
 * shares the name line, so a name and its door do not fight for one line;
 * with no phone on file the line holds the door alone. The lead comes
 * first, then each additional person in the order the record lists them.
 * Shared shape for the care-manager and SPICE-therapist cells on every
 * client.
 */
function CareTeamSlot({
  testId,
  label,
  pluralLabel,
  person,
  absentLabel,
  linkTo,
  nameTestId,
  phoneTestId,
  door,
  additional,
  additionalTestId,
  removeDoor,
  addDoor,
  notice,
}: {
  testId: string;
  label: string;
  /** The label when the slot holds more than one person. */
  pluralLabel: string;
  person: StaffPerson | null;
  absentLabel: string;
  /** Present only for care managers, who have a profile page to link to. */
  linkTo?: (person: StaffPerson) => string;
  nameTestId: string;
  phoneTestId: string;
  /** The lead's Assign/Change door. */
  door?: ReactNode;
  additional: readonly StaffPerson[];
  /** Each additional person's container; `-name` and `-phone` suffix it. */
  additionalTestId: string;
  /** Each additional person's Remove door, when the viewer has one. */
  removeDoor?: (person: StaffPerson) => ReactNode;
  /** The slot's Add door, when the viewer has one and the slot has a lead. */
  addDoor?: ReactNode;
  /** After a Remove: the Undo line or the refusal, under the Add line. */
  notice?: ReactNode;
}): ReactNode {
  const peopleCount = (person === null ? 0 : 1) + additional.length;
  const leadPhone = person?.phone ?? null;
  const leadHasPhone = leadPhone !== null && leadPhone !== '';
  return (
    <div data-testid={testId} className="min-w-0">
      <div className="flex min-h-[1.25rem] items-center">
        <span className="text-label tracking-label font-semibold uppercase text-sage-400">
          {peopleCount > 1 ? pluralLabel : label}
        </span>
      </div>
      <div data-testid={`${testId}-people`} className="flex flex-col gap-y-1">
        <div>
          <div className="mt-0.5 flex min-h-[1.375rem] flex-wrap items-center text-value">
            {person === null ? (
              <AbsentValue>{absentLabel}</AbsentValue>
            ) : linkTo ? (
              <Link to={linkTo(person)} data-testid={nameTestId} className={LINK_CLS}>
                {person.displayName}
              </Link>
            ) : (
              <span data-testid={nameTestId} className="font-medium text-sage-800">
                {person.displayName}
              </span>
            )}
          </div>
          {(leadHasPhone || door) && (
            <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5">
              <StaffPhoneLine phone={leadPhone} testId={phoneTestId} />
              {door && <span className="ml-auto flex items-center">{door}</span>}
            </div>
          )}
        </div>
        {additional.map((p) => {
          const hasPhone = p.phone !== null && p.phone !== '';
          const remove = removeDoor?.(p);
          return (
            <div key={p.rosterId} data-testid={additionalTestId}>
              <div className="flex min-h-[1.375rem] flex-wrap items-center text-value">
                {linkTo ? (
                  <Link to={linkTo(p)} data-testid={`${additionalTestId}-name`} className={LINK_CLS}>
                    {p.displayName}
                  </Link>
                ) : (
                  <span data-testid={`${additionalTestId}-name`} className="font-medium text-sage-800">
                    {p.displayName}
                  </span>
                )}
              </div>
              {(hasPhone || remove) && (
                <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5">
                  <StaffPhoneLine phone={p.phone} testId={`${additionalTestId}-phone`} />
                  {remove && <span className="ml-auto flex items-center">{remove}</span>}
                </div>
              )}
            </div>
          );
        })}
        {addDoor && (
          <div className="flex items-center">
            <span className="ml-auto flex items-center">{addDoor}</span>
          </div>
        )}
        {notice}
      </div>
    </div>
  );
}

export function CriticalStrip({
  record,
}: {
  record: ClientRecordSnapshot;
  /** Which face the record shows (recordFaceFor). The strip's own cells
   *  no longer vary by it — transport consent shows the same way on
   *  every face — but the caller still threads it through for the rest
   *  of the record page (the composer, the header note button). */
  face: RecordServiceSide;
}): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const [consentOpen, setConsentOpen] = useState(false);
  const [assignCmOpen, setAssignCmOpen] = useState(false);
  const [therapistEditOpen, setTherapistEditOpen] = useState(false);
  const [codeStatusEditOpen, setCodeStatusEditOpen] = useState(false);
  const [emergencyAddOpen, setEmergencyAddOpen] = useState(false);
  const [addStaffService, setAddStaffService] = useState<ClientStaffService | null>(null);
  // The escalation contact for failure copy, from the shell's cached
  // tenant config: the strip makes no read of its own.
  const contact = useSupportContact({ enabled: false });

  // After a Remove, each slot shows its outcome at its end for 30
  // seconds: the Undo line, or the refusal's sentence. The notice lives
  // here, not in the removed person's row, so the record's re-read (which
  // drops the person) leaves it; the timer is per slot and restarts on
  // each Remove.
  const [notices, setNotices] = useState<Partial<Record<ClientStaffService, RemoveNotice>>>({});
  const noticeTimers = useRef<Partial<Record<ClientStaffService, ReturnType<typeof setTimeout>>>>({});
  const undo = useAddClientStaff(tenantSlug);
  const queryClient = useQueryClient();
  function showNotice(service: ClientStaffService, notice: RemoveNotice): void {
    setNotices((prev) => ({ ...prev, [service]: notice }));
    clearTimeout(noticeTimers.current[service]);
    noticeTimers.current[service] = setTimeout(() => {
      setNotices((prev) => ({ ...prev, [service]: undefined }));
    }, REMOVE_NOTICE_MS);
  }
  useEffect(() => {
    const timers = noticeTimers.current;
    return () => {
      for (const timer of Object.values(timers)) clearTimeout(timer);
    };
  }, []);
  function onRemoved(service: ClientStaffService, person: StaffPerson): void {
    showNotice(service, { person, undo: true, error: null });
  }
  function onRemoveRefused(service: ClientStaffService, person: StaffPerson, err: unknown): void {
    showNotice(service, { person, undo: false, error: removeRefusal(err, person, contact) });
    // Already off: the record is re-read so the row leaves with the sentence.
    if (errorCodeOf(err) === 'assignment_not_found') {
      void queryClient.invalidateQueries({ queryKey: ['clientRecord'] });
    }
  }
  function pressUndo(service: ClientStaffService, person: StaffPerson): void {
    setNotices((prev) => ({ ...prev, [service]: { person, undo: true, error: null } }));
    undo.mutate(
      { id: clientId, rosterId: person.rosterId, service },
      {
        onSuccess: () => {
          clearTimeout(noticeTimers.current[service]);
          setNotices((prev) => ({ ...prev, [service]: undefined }));
        },
        onError: (err) =>
          setNotices((prev) => ({ ...prev, [service]: { person, undo: true, error: undoRefusal(err, person, service, contact) } })),
      },
    );
  }
  const noticeLine = (service: ClientStaffService): ReactNode => {
    const notice = notices[service];
    if (notice === undefined) return undefined;
    return <RemoveNoticeLine notice={notice} undoPending={undo.isPending} onUndo={() => pressUndo(service, notice.person)} />;
  };

  // Board-observer split (0152): a read-only viewer loses every door,
  // never a fact. Assign/Change CM, the SPICE-therapist door and Add/Remove
  // on additional staff are client-identity writes; Record/Change consent
  // is a clinical write.
  const { whoami } = useTenant();
  const canEditDetails = canEditClientDetails(whoami);
  // The care-manager doors (N4): manage_client_identity or the narrow
  // assign_care_managers; the SPICE therapist doors are everyday details.
  const canAssignCms = canAssignCareManagers(whoami);
  // Code status, the emergency contact and transport consent are
  // STRUCTURED-record facts, not notes: gated the same way the Care
  // record's cards gate their row writes (canWriteClinicalRecord), which
  // is narrower than write_clinical_notes: that is held broadly (any
  // staffer who posts notes), but keeping the structured record also
  // needs does_care_management or view_all_clinical. The server's
  // assertKeepsClinicalRecord refuses the same writes (LP-48).
  const canWriteRecord = canWriteClinicalRecord(whoami);
  // A SPICE-only viewer (does SPICE, not care management) never sees the
  // band's micro-actions — Assign/Change care manager, Add/Remove
  // additional staff, Add emergency contact, Record/Change transport
  // consent, Record/Change code status — the facts stay readable.
  const hideBandMicroActions = isSpiceOnlyViewer(whoami);

  const base = `/t/${tenantSlug}/clients/${clientId}`;

  // 1. Care team — FIRST. By engagement: a SPICE-only client has no care
  //    manager to nag for, so the slot becomes the SPICE therapists
  //    outright; a client on both services shows both, side by side; a
  //    care-management client shows only the care managers, as always.
  const owner = record.vitals.owner;
  const spiceTherapist = record.vitals.spiceTherapist;
  const additionalCareManagers = record.vitals.additionalCareManagers;
  const additionalSpiceTherapists = record.vitals.additionalSpiceTherapists;
  const isSpiceOnlyEngagement = record.vitals.engagement === 'spice';
  const isBothEngagement = record.vitals.engagement === 'both';
  const careManagerLink = (p: StaffPerson): string => `/t/${tenantSlug}/care-managers/${p.rosterId}`;

  const cmDoor = canAssignCms && !hideBandMicroActions && (
    <Button
      variant="ghost"
      size="sm"
      className={DOOR_BTN_CLS}
      data-testid="assign-cm-action"
      onClick={() => setAssignCmOpen(true)}
    >
      {owner ? 'Change' : 'Assign'}
    </Button>
  );
  // The same change affordance the record menu has, same gate: the door
  // opens the Change service dialog, which sets the service and its leads.
  const therapistDoor = canEditDetails && (
    <Button
      variant="ghost"
      size="sm"
      className={DOOR_BTN_CLS}
      data-testid="spice-therapist-action"
      onClick={() => setTherapistEditOpen(true)}
    >
      {spiceTherapist ? 'Change' : 'Assign'}
    </Button>
  );

  // Additional staff: Add once the slot has a lead, Remove on each
  // additional person, each behind the same gate as that slot's own lead
  // door. The care manager slot's doors stay hidden from a SPICE-only
  // viewer; the SPICE therapist slot's follow the identity gate alone.
  const canChangeCareManagers = canAssignCms && !hideBandMicroActions;
  const canChangeSpiceTherapists = canEditDetails;
  const addCareManagerDoor = canChangeCareManagers && owner !== null && (
    <Button
      variant="ghost"
      size="sm"
      className={DOOR_BTN_CLS}
      data-testid="add-care-manager-action"
      onClick={() => setAddStaffService('care_management')}
    >
      Add
    </Button>
  );
  const addSpiceTherapistDoor = canChangeSpiceTherapists && spiceTherapist !== null && (
    <Button
      variant="ghost"
      size="sm"
      className={DOOR_BTN_CLS}
      data-testid="add-spice-therapist-action"
      onClick={() => setAddStaffService('spice')}
    >
      Add
    </Button>
  );
  const removeCareManagerDoor = canChangeCareManagers
    ? (p: StaffPerson): ReactNode => (
        <RemoveStaffDoor
          person={p}
          service="care_management"
          testId="remove-additional-care-manager"
          onRemoved={(person) => onRemoved('care_management', person)}
          onRefused={(person, err) => onRemoveRefused('care_management', person, err)}
        />
      )
    : undefined;
  const removeSpiceTherapistDoor = canChangeSpiceTherapists
    ? (p: StaffPerson): ReactNode => (
        <RemoveStaffDoor
          person={p}
          service="spice"
          testId="remove-additional-spice-therapist"
          onRemoved={(person) => onRemoved('spice', person)}
          onRefused={(person, err) => onRemoveRefused('spice', person, err)}
        />
      )
    : undefined;

  const careManagerSlot = (
    <CareTeamSlot
      testId="strip-care-manager"
      label="Care manager"
      pluralLabel="Care managers"
      person={owner}
      absentLabel="Not assigned"
      linkTo={careManagerLink}
      nameTestId="vitals-cm-name"
      phoneTestId="strip-care-manager-phone"
      door={cmDoor}
      additional={additionalCareManagers}
      additionalTestId="strip-additional-care-manager"
      removeDoor={removeCareManagerDoor}
      addDoor={addCareManagerDoor}
      notice={noticeLine('care_management')}
    />
  );
  const spiceTherapistSlot = (
    <CareTeamSlot
      testId="strip-spice-therapist"
      label="SPICE therapist"
      pluralLabel="SPICE therapists"
      person={spiceTherapist}
      absentLabel="Not assigned"
      nameTestId="vitals-spice-therapist"
      phoneTestId="strip-spice-therapist-phone"
      door={therapistDoor}
      additional={additionalSpiceTherapists}
      additionalTestId="strip-additional-spice-therapist"
      removeDoor={removeSpiceTherapistDoor}
      addDoor={addSpiceTherapistDoor}
      notice={noticeLine('spice')}
    />
  );

  // 2. Emergency contacts + the authority marker. Up to TWO show, primary
  //    first (the array's own order — the aggregate reads ORDER BY
  //    created_at, so the earliest-recorded emergency contact leads,
  //    the same one a bare .find() used to pick as "the" contact); a
  //    third and beyond stay off-screen behind a "+N more" into People.
  //    The 0150 sibling ruling survives: the guardian reads in THIS
  //    cell's marker even when no emergency contact is recorded —
  //    authority may not vanish because nobody is flagged as the person
  //    to call.
  const emergencyContacts = record.contacts.filter((c) => c.isActive && c.isEmergency);
  const primaryEmergency = emergencyContacts[0] ?? null;
  const shownEmergencyContacts = emergencyContacts.slice(0, 2);
  const moreEmergencyContacts = emergencyContacts.length - shownEmergencyContacts.length;
  // A SPICE-only client must have an emergency contact on file: the
  // therapist visits alone and must be able to reach someone. On an
  // ACTIVE SPICE-only client the absence keeps the hollow vessel and
  // wears the clinical-warning ink; the door beside it stays the same
  // quiet "Add emergency contact" (never a block). An archived client is
  // not nagged, and a client on both services is left alone.
  const emergencyContactRequired = record.vitals.isActive && record.vitals.engagement === 'spice';
  const guardian = record.contacts.find((c) => c.isActive && c.isGuardian) ?? null;
  // Authority roles (Guardian / POA / HC Proxy) describe the PRIMARY
  // emergency contact — the one person this marker has always described.
  // A second contact showing beside her doesn't change what these words
  // mean; it isn't asked to disambiguate among several people's roles.
  const authorityItems: ReactNode[] = [];
  if (guardian) {
    authorityItems.push(
      guardian.id === primaryEmergency?.id ? (
        <span key="guardian">Guardian</span>
      ) : (
        <span key="guardian">Guardian {guardian.name}</span>
      ),
    );
  }
  if (primaryEmergency?.isPoa) {
    authorityItems.push(
      <span key="poa" title={ACRONYM_TITLES.poa}>
        POA
      </span>,
    );
  }
  if (primaryEmergency?.isHcp) {
    authorityItems.push(
      <span key="hcp" title={ACRONYM_TITLES.hc_proxy}>
        HC Proxy
      </span>,
    );
  }

  // 3. Code status + instruments. The 0136 SET semantics hold: null or
  //    ['unknown'] both read "Not recorded" (the honest state) — as the
  //    absent chip now, with the on-file POLST beside it in the marker.
  const { codeStatus, transportationConsent } = record.vitals;
  const codeStatusRecorded =
    codeStatus !== null && codeStatus.values.some((v) => v !== 'unknown');
  const onFile = (instrument: string): boolean =>
    record.legalInstruments.some(
      (l) => l.isActive && l.instrument === instrument && l.status === 'on_file',
    );
  const polstOnFile = onFile('polst');
  const dpoaOnFile = onFile('dpoa');
  // The row the strip's Change door edits — mirrors the server's OWN
  // pick (pickCodeStatus, api/_shared/clientRecordSnapshot.ts): the
  // active code_status row if it carries a value, else the active polst
  // row if THAT carries one. Keyed off more than instrument==='code_status'
  // because a client's code status can live on the polst row alone.
  const codeStatusCandidates = record.legalInstruments.filter(
    (l) =>
      l.isActive &&
      (l.instrument === 'code_status' || l.instrument === 'polst') &&
      l.codeStatusValues !== undefined &&
      l.codeStatusValues.length > 0,
  );
  const codeStatusRow =
    codeStatusCandidates.find((l) => l.instrument === 'code_status') ??
    codeStatusCandidates.find((l) => l.instrument === 'polst') ??
    null;
  const codeStatusDoor = canWriteRecord && !hideBandMicroActions && (
    <Button
      variant="ghost"
      size="sm"
      className={DOOR_BTN_CLS}
      data-testid="code-status-action"
      onClick={() => setCodeStatusEditOpen(true)}
    >
      {codeStatusRow ? 'Change' : 'Record'}
    </Button>
  );

  // 4. Transport consent — the care manager's fact (it gates driving),
  //    but SPICE plans around it too, so it renders the same way
  //    regardless of face.

  // 5. Allergies — LAST. Active rows only (archived rows leave the
  //    screen); the confirmed-none marker is server-exclusive with them.
  //    Most severe first, then alphabetical — the ONE order every
  //    allergy list on the record shares (src/lib/allergySort.ts): this
  //    strip, the Care record's card, the Face Sheet, and the
  //    Medication List's allergy line.
  const activeAllergies = record.allergies.filter((r) => r.isActive);
  const sortedActiveAllergies = sortAllergiesBySeverity(activeAllergies);
  const allergiesConfirmedNone =
    activeAllergies.length === 0 && record.allergiesNoneKnownOn !== null;

  return (
    <>
      {/* Row one of the merged vitals band: one background, one rhythm.
          Desktop = the weighted five-column instrument panel with
          hairline dividers (widest cell to the emergency contact); phone
          = a single column of even two-line cells. The strip scrolls
          sideways inside its own box where its cells outgrow it (large
          text on a narrow desktop), so it never widens the page. */}
      <div
        data-testid="critical-strip"
        className={
          'px-4 py-3 border-b border-sage-800/[8%] grid gap-y-3 overflow-x-auto ' +
          'sm:grid-cols-2 sm:gap-x-6 ' +
          'lg:grid-cols-[0.9fr_1.42fr_0.75fr_1.12fr_0.93fr] lg:gap-y-0 lg:gap-x-0 ' +
          'lg:divide-x lg:divide-sage-800/[8%]'
        }
      >
        {isBothEngagement ? (
          // Both services: the care managers and the SPICE therapists side
          // by side in the one first slot. The pair's container carries the
          // minimum width that keeps each slot's label, names and phone
          // lines whole from xl up (the first grid track grows to it);
          // each slot's own floor makes the pair stack, not squeeze,
          // wherever the cell is narrower than two slots.
          <div
            data-testid="strip-care-team"
            className="min-w-0 xl:min-w-[20rem] lg:px-5 lg:first:pl-0 lg:last:pr-0"
          >
            <div className="flex flex-wrap gap-x-3 gap-y-3">
              <div className="min-w-[8rem] flex-1">{careManagerSlot}</div>
              <div className="min-w-[8rem] flex-1">{spiceTherapistSlot}</div>
            </div>
          </div>
        ) : isSpiceOnlyEngagement ? (
          // SPICE only: the slot IS the therapists — never "Care manager:
          // SPICE only".
          <div className="min-w-0 lg:px-5 lg:first:pl-0 lg:last:pr-0">{spiceTherapistSlot}</div>
        ) : (
          // Care management only.
          <div className="min-w-0 lg:px-5 lg:first:pl-0 lg:last:pr-0">{careManagerSlot}</div>
        )}

        <FactCell
          testId="strip-emergency"
          label="Emergency contact"
          door={
            emergencyContacts.length === 0 &&
            canWriteRecord &&
            !hideBandMicroActions && (
              // Opens Add contact in place, with Emergency contact ticked;
              // the same gate as the Contacts card's own Add.
              <Button
                variant="ghost"
                size="sm"
                className={DOOR_BTN_CLS}
                data-testid="strip-emergency-add"
                onClick={() => setEmergencyAddOpen(true)}
              >
                Add emergency contact
              </Button>
            )
          }
        >
          {shownEmergencyContacts.length > 0 ? (
            <>
              <span
                data-testid="strip-emergency-list"
                className="flex flex-wrap items-center gap-x-3 gap-y-0.5"
              >
                {shownEmergencyContacts.map((c) => {
                  // The contact's own phone, else its Directory entry's (0227).
                  const phone = contactEffective(c).phone;
                  return (
                    <span key={c.id} className="inline-flex flex-wrap items-center gap-x-1.5">
                      <Link to={`${base}?tab=people`} className={LINK_CLS}>
                        {c.name}
                      </Link>
                      {(c.relationship || phone) && (
                        <span className={`${MARKER_CLS} text-ink-soft`}>
                          {[c.relationship, phone ? formatPhoneDisplay(phone) : null]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      )}
                    </span>
                  );
                })}
              </span>
              {moreEmergencyContacts > 0 && (
                <Link
                  to={`${base}?tab=people`}
                  data-testid="strip-emergency-more"
                  className={DOOR_LINK_CLS}
                >
                  +{moreEmergencyContacts} more
                </Link>
              )}
            </>
          ) : emergencyContactRequired ? (
            <Chip variant="clinical-warn" className={STRIP_CHIP} data-testid="strip-emergency-required">
              None recorded
            </Chip>
          ) : (
            <AbsentValue>None recorded</AbsentValue>
          )}
          {authorityItems.length > 0 && (
            // Authority roles: affirm-deep WORDS at the marker tier —
            // confirmed on-file facts spend ink, never boxes.
            <span
              data-testid="strip-emergency-flags"
              className={`${MARKER_CLS} font-medium text-affirm-deep flex flex-wrap items-center gap-x-1.5`}
            >
              {authorityItems.map((item, i) => (
                <span key={i} className="flex items-center gap-x-1.5">
                  {i > 0 && <span aria-hidden="true">·</span>}
                  {item}
                </span>
              ))}
            </span>
          )}
        </FactCell>

        <FactCell testId="strip-code-status" label="Code status" door={codeStatusDoor}>
          {codeStatus !== null && codeStatusRecorded ? (
            <Chip variant="neutral" className={STRIP_CHIP} data-testid="strip-code-status-value">
              {codeStatus.values.map((v, i) => (
                <span key={v}>
                  {i > 0 && ' + '}
                  <span title={ACRONYM_TITLES[v]}>{codeStatusSetLabel([v])}</span>
                </span>
              ))}
            </Chip>
          ) : (
            <AbsentValue>Not recorded</AbsentValue>
          )}
          {(polstOnFile || dpoaOnFile) && (
            <span
              data-testid="strip-instruments"
              className={`${MARKER_CLS} font-medium text-affirm-deep`}
            >
              {polstOnFile && <span title={ACRONYM_TITLES.polst}>POLST on file</span>}
              {polstOnFile && dpoaOnFile && ' · '}
              {dpoaOnFile && <span title={ACRONYM_TITLES.dpoa}>DPOA on file</span>}
            </span>
          )}
        </FactCell>

        <FactCell
          testId="strip-transportation"
          label="Transport consent"
          door={
            canWriteRecord &&
            !hideBandMicroActions && (
              <Button
                variant="ghost"
                size="sm"
                className={DOOR_BTN_CLS}
                data-testid="transportation-consent-action"
                onClick={() => setConsentOpen(true)}
              >
                {transportationConsent === null ? 'Record' : 'Change'}
              </Button>
            )
          }
        >
          {transportationConsent === null ? (
            <AbsentValue>Not recorded</AbsentValue>
          ) : transportationConsent.value === 'declined' ? (
            <Chip variant="risk" className={STRIP_CHIP}>
              Declined
            </Chip>
          ) : (
            <Chip variant="affirm" className={STRIP_CHIP}>
              {CONSENT_LABELS[transportationConsent.value]}
            </Chip>
          )}
        </FactCell>

        <FactCell testId="strip-allergies" label="Allergies">
          {sortedActiveAllergies.length > 0 ? (
            // Dangerous CONTENT: the clinical-warning ink at the one value
            // weight; the ink alone is the signal. Tap-through to the
            // owning card. Most severe first (sortedActiveAllergies);
            // a stated severity rides after its substance as a marker.
            <Link
              to={`${base}?tab=care#allergies`}
              data-testid="strip-allergies-list"
              className={
                'font-medium text-clinical-warn underline-offset-2 hover:underline hover:decoration-clinical-warn ' +
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-tight'
              }
            >
              {sortedActiveAllergies
                .slice(0, 2)
                .map((r) => {
                  const marker = allergySeverityMarker(r.severity);
                  const substance = displayValue(r.substance, 'allergy');
                  return marker ? `${substance} · ${marker}` : substance;
                })
                .join(', ')}
              {sortedActiveAllergies.length > 2 && ` +${sortedActiveAllergies.length - 2}`}
            </Link>
          ) : allergiesConfirmedNone ? (
            // Asked and answered NONE: a confirmed positive → affirm pill.
            <Chip
              variant="affirm"
              className={STRIP_CHIP}
              data-testid="strip-allergies-none-known"
            >
              No known allergies
            </Chip>
          ) : (
            <AbsentValue testId="strip-allergies-empty">Not recorded</AbsentValue>
          )}
        </FactCell>
      </div>

      {consentOpen && (
        <TransportationConsentDialog
          current={transportationConsent?.value ?? null}
          pronouns={record.vitals.pronouns}
          onClose={() => setConsentOpen(false)}
        />
      )}

      {assignCmOpen && (
        <AssignCmDialog
          current={owner}
          pronouns={record.vitals.pronouns}
          contact={contact}
          onClose={() => setAssignCmOpen(false)}
        />
      )}

      {therapistEditOpen && (
        <ChangeServiceDialog vitals={record.vitals} onClose={() => setTherapistEditOpen(false)} />
      )}

      {addStaffService !== null && (
        <AddClientStaffDialog
          service={addStaffService}
          onClientRosterIds={
            addStaffService === 'care_management'
              ? [...(owner ? [owner.rosterId] : []), ...additionalCareManagers.map((p) => p.rosterId)]
              : [...(spiceTherapist ? [spiceTherapist.rosterId] : []), ...additionalSpiceTherapists.map((p) => p.rosterId)]
          }
          pronouns={record.vitals.pronouns}
          contact={contact}
          onClose={() => setAddStaffService(null)}
        />
      )}

      {codeStatusEditOpen && (
        // The SAME registry-derived dialog the Legal & wishes card opens
        // for this exact row (cardVariants.tsx's LegalInstrumentsCard),
        // as a code-status dialog: the set, Status and Effective only. A
        // recorded row PATCHes, preselected on its current SET, and never
        // sends its instrument; with none on file the add is seeded with
        // the code_status instrument (the writer upserts per client and
        // instrument).
        <RowEditDialog
          title="Code status"
          intro={codeStatusIntro(record.vitals.pronouns)}
          primaryLabel="Save code status"
          fields={CODE_STATUS_FIELDS}
          projection="legal-instruments"
          rowId={codeStatusRow?.id ?? ''}
          values={
            codeStatusRow
              ? registryValuesFromRow({ ...codeStatusRow }, 'legal-instruments')
              : { instrument: 'code_status' }
          }
          mode={codeStatusRow ? 'edit' : 'create'}
          onClose={() => setCodeStatusEditOpen(false)}
        />
      )}

      {emergencyAddOpen && (
        // The Contacts card's own Add dialog, opened in place with
        // Emergency contact ticked.
        <RowEditDialog
          mode="create"
          title="Add contact"
          projection="contacts"
          rowId=""
          values={{ is_emergency: true }}
          onClose={() => setEmergencyAddOpen(false)}
        />
      )}
    </>
  );
}
